import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import axios from 'axios';
import JoditEditor from 'jodit-react';
import { Base_Url } from '../config';
import { useParams, useNavigate } from 'react-router-dom';

const EditVersionDownloadForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    softwareName: '',
    versions: [],
  });
  const [content, setContent] = useState('');
  const editor = useRef(null);

  // Editor configuration
 const editorConfig = useMemo(() => ({
     readonly: false,
     minHeight: 400,
     editHTMLDocumentMode: true,
     defaultMode: 1,
     enter: 'P',
     useSearch: true,
     buttons: [
       'source', '|', 'bold', 'italic', 'underline', 'strikethrough', 'eraser',
       '|', 'ul', 'ol', 'indent', 'outdent', '|', 'font', 'fontsize',
       'paragraph', 'lineHeight', 'brush', '|', 'superscript', 'subscript',
       'classSpan', '|', 'file', 'image', 'video', '|', 'spellcheck',
       'speechRecognize', '|', 'cut', 'copy', 'paste', 'selectall',
       'copyformat', '|', 'hr', 'table', 'link', 'symbols', '|',
       'ai-commands', 'ai-assistant', '|', 'left', '|', 'undo', 'redo',
       '|', 'find', 'fullsize', 'preview', 'print', 'about'
     ],
     buttonsXS: [
       'ul',
       'ol',
       'paragraph',
       'source'
     ],
     controls: {
       ul: {
         list: {
           default: 'Default',
           circle: 'Circle',
           disc: 'Disc',
           square: 'Square',
         },
       },
     },
     list: {
       indent: true,
       startIndent: 20,
     },
     toolbarAdaptive: false,
     toolbarSticky: true,
     addNewLineOnDBLClick: true,
     toolbarButtonSize: 'middle',
     style: {
       ul: {
         'list-style-type': 'disc',
         'margin-left': '20px',
         'padding-left': '20px'
       },
       li: {
         'list-style': 'inherit',
         'margin-left': '10px'
       }
     },
   }), []);

  const fetchVersionsWithPlatforms = async (versionsData) => {
    // console.log('Starting to process versions:', versionsData);
    
    if (!Array.isArray(versionsData)) {
      console.error('versionsData is not an array:', versionsData);
      return [];
    }
  
    try {
      const versionsWithPlatforms = await Promise.all(
        versionsData.map(async (version) => {
          if (!version || !version.id) {
            console.error('Invalid version object:', version);
            return null;
          }
  
          try {
            // console.log(`Fetching platforms for version ${version.id}`);
            // Fetch platforms for the current version
            const platformsRes = await axios.get(
              `${Base_Url}/platform/version/${version.id}`,
            );
            
            if (!platformsRes.data) {
              console.error('No platform data received for version:', version.id);
              return { ...version, platforms: [] };
            }
  

            // console.log(platformsRes)
            const platformsWithDisks = await Promise.all(
              platformsRes.data.map(async (platform) => {
                if (!platform || !platform.id) {
                  console.error('Invalid platform object:', platform);
                  return null;
                }
  
                try {
                  // console.log(`Fetching disks for platform ${platform.id}`);
                  // Fetch disks for the current platform
                  const disksRes = await axios.get(
                    `${Base_Url}/disk/platform/${platform.id}`,
                  );
  
                  return {
                    ...platform,
                    disks: (disksRes.data || []).filter(
                      disk => disk && disk.platform_id === platform.id
                    ),
                  };
                } catch (diskError) {
                  console.error(
                    `Error fetching disks for platform ${platform.id}:`,
                    diskError.message
                  );
                  return { ...platform, disks: [] };
                }
              })
            );
  
            const validPlatforms = platformsWithDisks.filter(platform => platform !== null);
  
            return {
              ...version,
              platforms: validPlatforms,
            };
          } catch (platformError) {
            console.error(
              `Error fetching platforms for version ${version.id}:`,
              platformError.message
            );
            return { ...version, platforms: [] };
          }
        })
      );
  
      return versionsWithPlatforms.filter(version => version !== null);
    } catch (error) {
      console.error('Error in fetchVersionsWithPlatforms:', error);
      return [];
    }
  };
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [softwareRes, versionsRes, sciSoftPageRes] = await Promise.all([
          axios.get(`${Base_Url}/software/${id}`),
          axios.get(`${Base_Url}/version/software/${id}`), // Fetch versions by software_id
          axios.get(`${Base_Url}/scisoftpage/software/${id}`) // Fetch SciSoftPage by software_id
        ]);
  
        // console.log('Initial API responses:', {
        //   software: softwareRes.data,
        //   versions: versionsRes.data,
        //   sciSoftPage: sciSoftPageRes.data
        // });
  
        // Ensure versionsData is always an array
        const versionsData = Array.isArray(versionsRes.data) ? versionsRes.data : [versionsRes.data];
  
        const versionsWithPlatforms = await fetchVersionsWithPlatforms(versionsData);
  
        setFormData({
          softwareName: softwareRes.data.name,
          versions: versionsWithPlatforms,
        });
        setContent(sciSoftPageRes.data?.content || '');
      } catch (error) {
        console.error('Error in fetchData:', error);
      }
    };
  
    fetchData();
  }, [id]);

  // Add new version
  const addVersion = async () => {
    const versionName = prompt('Enter the version name:'); // Prompt for version name
    if (!versionName) return; // Exit if no name is provided
  
    try {
      // Create a new version in the database
      const response = await axios.post(`${Base_Url}/version`, {
        software_id: id,
        version_name: versionName,
      });
  
      // Get the new version's ID from the response
      const newVersion = {
        id: response.data.id,
        version_name: versionName,
        platforms: [], // Initialize with an empty array of platforms
      };
  
      // Update the state with the new version
      setFormData((prev) => ({
        ...prev,
        versions: [...prev.versions, newVersion],
      }));
    } catch (error) {
      console.error('Error adding version:', error);
    }
  };

  // Add new platform
  const addPlatform = async (versionIndex) => {
    const version = formData.versions[versionIndex];

    try {
      // Create a new platform in the database
      const response = await axios.post(`${Base_Url}/platform`, {
        version_id: version.id,
        platform_name: '', // Empty initial name
      });

      // Add database ID to the new platform
      const newPlatform = {
        id: response.data.id,
        platform_name: '',
        disks: [],
      };

      // Update state with new platform
      setFormData((prev) => ({
        ...prev,
        versions: prev.versions.map((v, i) =>
          i === versionIndex
            ? {
                ...v,
                platforms: [...(v.platforms || []), newPlatform],
              }
            : v
        ),
      }));
    } catch (error) {
      console.error('Error adding platform:', error);
    }
  };

  // Add new disk
  const addDisk = async (versionIndex, platformIndex) => {
    const platform = formData.versions[versionIndex].platforms[platformIndex];
    const platformId = platform.id;
  
    try {
      const response = await axios.post(`${Base_Url}/disk`, {
        platform_id: platformId,
        disk_number: platform.disks.length + 1,
        disk_size: '', // Empty string
        download_link: '', // Empty string
      });
  
      const newDisk = {
        id: response.data.id,
        platform_id: platformId,
        disk_number: platform.disks.length + 1,
        disk_size: '', // Empty string
        download_link: '', // Empty string
      };
  
      setFormData((prev) => ({
        ...prev,
        versions: prev.versions.map((version, i) =>
          i === versionIndex
            ? {
                ...version,
                platforms: version.platforms.map((platform, j) =>
                  j === platformIndex
                    ? {
                        ...platform,
                        disks: [...platform.disks, newDisk],
                      }
                    : platform
                ),
              }
            : version
        ),
      }));
    } catch (error) {
      console.error('Error adding disk:', error);
    }
  };

  // Update disk
 // Update disk
const updateDisk = async (versionIndex, platformIndex, diskIndex, field, value) => {
  // First, update the state
  setFormData((prev) => ({
    ...prev,
    versions: prev.versions.map((version, i) =>
      i === versionIndex
        ? {
            ...version,
            platforms: version.platforms.map((platform, j) =>
              j === platformIndex
                ? {
                    ...platform,
                    disks: platform.disks.map((disk, k) =>
                      k === diskIndex ? { ...disk, [field]: value } : disk
                    ),
                  }
                : platform
            ),
          }
        : version
    ),
  }));

  // Get the updated disk
  const disk = formData.versions[versionIndex].platforms[platformIndex].disks[diskIndex];

  // Validate the disk data
  if (!disk.platform_id) {
    console.error('Platform ID is missing. Cannot update disk without a valid platform.');
    return;
  }

  if (!disk.disk_size || !disk.download_link) {
    console.error('Disk size and download link are required');
    return;
  }

  // Log the request payload
  // console.log('Updating disk with payload:', disk);

  // Make the API call
  if (disk.id) {
    try {
      const response = await axios.put(`${Base_Url}/disk/${disk.id}`, {
        ...disk,
        [field]: value,
      });
      // console.log('Disk update response:', response.data);
    } catch (error) {
      console.error('Error updating disk:', error.response ? error.response.data : error.message);
    }
  }
};

  // Remove version
  const removeVersion = async (versionIndex) => {
    const version = formData.versions[versionIndex];
    if (version.id) {
      try {
        await axios.delete(`${Base_Url}/version/${version.id}`);
      } catch (error) {
        console.error('Error deleting version:', error);
      }
    }

    setFormData((prev) => ({
      ...prev,
      versions: prev.versions.filter((_, i) => i !== versionIndex),
    }));
  };

  // Remove platform
  const removePlatform = async (versionIndex, platformIndex) => {
    const platform = formData.versions[versionIndex].platforms[platformIndex];
    if (platform.id) {
      try {
        await axios.delete(`${Base_Url}/platform/${platform.id}`);
      } catch (error) {
        console.error('Error deleting platform:', error);
      }
    }

    setFormData((prev) => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.filter((_, j) => j !== platformIndex),
            }
          : version
      ),
    }));
  };

  // Add this new function to handle platform name updates
  const updatePlatform = async (versionIndex, platformIndex, newPlatformName) => {
    const platform = formData.versions[versionIndex].platforms[platformIndex];
    
    // First update the state
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((v, i) =>
        i === versionIndex
          ? {
              ...v,
              platforms: v.platforms.map((p, j) =>
                j === platformIndex
                  ? { ...p, platform_name: newPlatformName }
                  : p
              )
            }
          : v
      )
    }));

    // Then update in database if platform has an ID
    if (platform.id) {
      try {
        await axios.put(`${Base_Url}/platform/${platform.id}`, {
          platform_name: newPlatformName,
          version_id: platform.version_id
        });
      } catch (error) {
        console.error('Error updating platform:', error);
      }
    }
  };

  // Remove disk
  const removeDisk = async (versionIndex, platformIndex, diskIndex) => {
    const disk = formData.versions[versionIndex].platforms[platformIndex].disks[diskIndex];
    if (disk.id) {
      try {
        await axios.delete(`${Base_Url}/disk/${disk.id}`);
      } catch (error) {
        console.error('Error deleting disk:', error);
      }
    }

    setFormData((prev) => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.map((platform, j) =>
                j === platformIndex
                  ? {
                      ...platform,
                      disks: platform.disks.filter((_, k) => k !== diskIndex),
                    }
                  : platform
              ),
            }
          : version
      ),
    }));
  };

  // Form submission handler
 // Form submission handler
const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    // Update software name
    await axios.put(`${Base_Url}/software/${id}`, {
      name: formData.softwareName,
    });

    // Update versions, platforms, and disks
    for (const version of formData.versions) {
      if (version.id) {
        // Update existing version
        await axios.put(`${Base_Url}/version/${version.id}`, {
          version_name: version.version_name,
          software_id: id,
        });
      } else {
        // Create new version
        const versionRes = await axios.post(`${Base_Url}/version`, {
          software_id: id,
          version_name: version.version_name,
        });
        version.id = versionRes.data.id;
      }

      // Update platforms and disks
      for (const platform of version.platforms) {
        if (platform.id) {
          await axios.put(`${Base_Url}/platform/${platform.id}`, {
            platform_name: platform.platform_name,
            version_id: version.id,
          });
        } else {
          const platformRes = await axios.post(`${Base_Url}/platform`, {
            version_id: version.id,
            platform_name: platform.platform_name,
          });
          platform.id = platformRes.data.id;
        }

        for (const disk of platform.disks) {
          if (disk.id) {
            await axios.put(`${Base_Url}/disk/${disk.id}`, {
              platform_id: platform.id, // Ensure platform_id is included
              disk_size: disk.disk_size,
              download_link: disk.download_link,
              disk_number: disk.disk_number,
            });
          } else {
            await axios.post(`${Base_Url}/disk`, {
              platform_id: platform.id, // Ensure platform_id is included
              disk_number: disk.disk_number,
              disk_size: disk.disk_size,
              download_link: disk.download_link,
            });
          }
        }
      }
    }

    // Update setup guide
    await axios.put(`${Base_Url}/scisoftpages/${id}`, {

      software_id: id,
      content: content,
    });

    navigate('/services-software-scientific');
  } catch (error) {
    console.error('Error updating data:', error);
  }
};

  return (
    <div className="container py-4">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Edit Version Download</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Software Name */}
            <div className="mb-3">
              <label className="form-label">Software Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.softwareName}
                onChange={(e) => setFormData(prev => ({ ...prev, softwareName: e.target.value }))}
                required
              />
            </div>

            {/* Versions */}
            {formData.versions.map((version, versionIndex) => (
              <div key={versionIndex} className="card mb-3">
                <div className="card-body">
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      value={version.version_name}
                      onChange={(e) => {
                        const newVersions = [...formData.versions];
                        newVersions[versionIndex].version_name = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          versions: newVersions,
                        }));
                      }}
                      placeholder="Version Name"
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => removeVersion(versionIndex)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Platforms */}
                  {version.platforms && version.platforms.map((platform, platformIndex) => (
                    <div key={platformIndex} className="card mb-3">
                      <div className="card-body">
                        <div className="input-group mb-3">
                          <input
                            type="text"
                            className="form-control"
                            value={platform.platform_name || ''}
                            onChange={(e) => updatePlatform(versionIndex, platformIndex, e.target.value)}
                            placeholder="e.g. Windows, Linux ,Mac"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => removePlatform(versionIndex, platformIndex)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        {/* Disks */}
                        {platform.disks && platform.disks.map((disk, diskIndex) => (
                          <div key={diskIndex} className="input-group mb-2">
                            <input
                              type="text"
                              className="form-control"
                              value={disk.disk_size || ''}
                              onChange={(e) => updateDisk(versionIndex, platformIndex, diskIndex, 'disk_size', e.target.value)}
                              placeholder="Disk Size"
                              required
                            />
                            <input
                              type="url"
                              className="form-control"
                              value={disk.download_link || ''}
                              onChange={(e) => updateDisk(versionIndex, platformIndex, diskIndex, 'download_link', e.target.value)}
                              placeholder="Download Link"
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeDisk(versionIndex, platformIndex, diskIndex)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="btn btn-outline-primary w-100 mt-2"
                          onClick={() => addDisk(versionIndex, platformIndex)}
                        >
                          <PlusCircle size={16} className="me-2" />
                          Add Disk
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    onClick={() => addPlatform(versionIndex)}
                  >
                    <PlusCircle size={16} className="me-2" />
                    Add Platform
                  </button>
                </div>
              </div>
            ))}

<button
  type="button"
  className="btn btn-outline-primary w-100 mb-3"
  onClick={() => {
     addVersion();
  }}
>
  <PlusCircle size={16} className="me-2" />
  Add Version
</button>

            {/* Setup Guide */}
            <div className="mb-3">
              <label className="form-label">Setup Guide</label>
              <div className="editor-container">
                <JoditEditor
                  ref={editor}
                  value={content}
                  config={editorConfig}
                  tabIndex={1}
                  onBlur={(newContent) => setContent(newContent)}
                  onChange={() => {}}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className="btn btn-primary w-100">
              Save Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditVersionDownloadForm;